// MIRRORED: the workbench's `shared/modelFit.ts` (branch d277-runner-configure, tip 67a41fa8), byte for byte down to "AIOE additions"; change both sides together.
/**
 * "Will this model run on this machine?" — the pure estimate behind the Local models hub's fit badge
 * (src/llm/localRunner/fit.ts wraps it with this machine's GPU and RAM), and what the organisation's
 * console uses to say which models a machine can run from the `hardware` it reports (D277,
 * shared/schemas/runnerOrders.ts). AIOE keeps the same code as its `shared/modelFit.ts` (AIOE 0.41.0):
 * the same names and signatures, so both sides give the same verdict for the same hardware. Pure: no
 * Node import.
 *
 * Deliberately approximate. The weights dominate and their size is known exactly; the KV cache depends
 * on layer count, head count and head dimension, which are inside the GGUF and not known until it is
 * downloaded. So the KV term is a bucketed estimate and every verdict is reported as an estimate, not a
 * promise. It exists to stop someone spending an hour downloading 113 GiB onto a 16 GB laptop, not to
 * predict resident memory to the megabyte.
 */
const MiB = 1024 * 1024;
const GiB = 1024 * MiB;
/** The context a model is judged at when nothing says otherwise (the catalogue's `contextLength` wins). */
export const DEFAULT_FIT_CONTEXT = 8192;
/**
 * KV cache bytes at f16, bucketed by model size.
 *
 * Measured per-token costs across current architectures run roughly 56–144 KiB for small models and
 * climb with depth; grouped-query attention is why it is not simply proportional to parameter count.
 * These buckets sit near the top of the observed range so the estimate errs toward warning.
 */
export function estimateKvBytes(weightsBytes, contextLength) {
    const perToken = weightsBytes <= 2 * GiB ? 96 * 1024 : weightsBytes <= 6 * GiB ? 128 * 1024 : weightsBytes <= 16 * GiB ? 192 * 1024 : weightsBytes <= 48 * GiB ? 320 * 1024 : 512 * 1024;
    return Math.round(perToken * Math.max(contextLength, 0));
}
/** Compute buffers and the runtime itself — a floor plus a fraction of the weights. */
export function estimateOverheadBytes(weightsBytes) {
    return Math.max(512 * MiB, Math.round(weightsBytes * 0.1));
}
/** GPU and system memory are one pool, so "GPU memory" is not a separate budget. */
export function isUnifiedMemory(gpu) {
    // Windows spells it "AMD Radeon(TM) 8060S Graphics": the trademark sits between the two words.
    return gpu?.vendor === "apple" || (gpu?.vendor === "amd" && /strix|radeon(?:\s*\(tm\))?\s*8\d{3}|ryzen ai max/i.test(gpu.name ?? ""));
}
function human(bytes) {
    if (bytes >= GiB)
        return `${(bytes / GiB).toFixed(bytes >= 10 * GiB ? 0 : 1)} GB`;
    return `${Math.round(bytes / MiB)} MB`;
}
export function estimateFit(input) {
    const { weightsBytes, contextLength, ramBytes } = input;
    const kvCacheBytes = estimateKvBytes(weightsBytes, contextLength);
    const overheadBytes = estimateOverheadBytes(weightsBytes);
    const requiredBytes = weightsBytes + kvCacheBytes + overheadBytes;
    const unified = input.unified === true;
    const gpuBytes = input.gpuBytes && input.gpuBytes > 0 && !unified ? input.gpuBytes : undefined;
    const base = { requiredBytes, weightsBytes, kvCacheBytes, overheadBytes, gpuBytes, ramBytes, unified };
    if (!Number.isFinite(weightsBytes) || weightsBytes <= 0 || !Number.isFinite(ramBytes) || ramBytes <= 0) {
        return { ...base, verdict: "unknown", summary: "Size unknown — cannot estimate whether this fits." };
    }
    const need = human(requiredBytes);
    // Leave the GPU a tenth for the desktop and the driver's own allocations.
    if (gpuBytes && requiredBytes <= gpuBytes * 0.9) {
        return { ...base, verdict: "fits-gpu", summary: `≈${need} — fits in ${human(gpuBytes)} of GPU memory at ${contextLength.toLocaleString()} context.` };
    }
    if (requiredBytes <= ramBytes * 0.7) {
        const where = unified ? "unified memory" : "system RAM";
        return { ...base, verdict: "fits-ram", summary: `≈${need} — fits in ${human(ramBytes)} of ${where}${gpuBytes ? ", but not in GPU memory" : ""}.` };
    }
    if (requiredBytes <= ramBytes * 0.95) {
        return { ...base, verdict: "tight", summary: `≈${need} of ${human(ramBytes)} — it will run, but leaves little for anything else.` };
    }
    return { ...base, verdict: "too-big", summary: `≈${need} needed but this machine has ${human(ramBytes)} — it will not load.` };
}
/** The largest context that still fits the given budget, rounded down to 1k. */
export function maxContextFor(weightsBytes, budgetBytes) {
    const spare = budgetBytes - weightsBytes - estimateOverheadBytes(weightsBytes);
    if (spare <= 0)
        return 0;
    // estimateKvBytes is a step function of weight size, so one probe gives the rate.
    const perToken = estimateKvBytes(weightsBytes, 1024) / 1024;
    return Math.max(0, Math.floor(spare / perToken / 1024) * 1024);
}
/**
 * The estimate for a machine as it reported itself: against the GPU with the most memory (what the
 * runner's Automatic choice takes among discrete cards), unified when the machine said so or its GPU is
 * one that shares memory. What the console uses to say which models a node can run.
 */
export function fitForDevice(hardware, weightsBytes, contextLength = DEFAULT_FIT_CONTEXT) {
    const best = [...hardware.gpus].sort((a, b) => (b.vramBytes ?? 0) - (a.vramBytes ?? 0))[0];
    const unified = hardware.unified ?? isUnifiedMemory(best);
    return estimateFit({ weightsBytes, contextLength, ramBytes: hardware.ramBytes, ...(best?.vramBytes ? { gpuBytes: best.vramBytes } : {}), unified });
}
// ── AIOE additions (not in the workbench's copy) ─────────────────────
/** Bytes in words, as the estimate says them: "21 GB", "6.4 GB", "800 MB". */
export function fitHuman(bytes) {
    return human(bytes);
}
/** The memory the model had to fit in: the GPU's when there is one the model could have used, else the machine's RAM. */
export function fitBudgetBytes(fit) {
    return fit.gpuBytes && fit.gpuBytes > fit.ramBytes ? fit.gpuBytes : fit.ramBytes;
}
/** The refusal in words: "needs about 21 GB, mju-gpu-2 has 16 GB". */
export function tooBigWords(fit, machine) {
    return `needs about ${fitHuman(fit.requiredBytes)}, ${machine} has ${fitHuman(fitBudgetBytes(fit))}`;
}
//# sourceMappingURL=modelFit.js.map